(function () {
  'use strict';

  var root = document.documentElement;
  var attributeName = 'data-vcc-theme';
  var defaultAttributeName = 'data-vcc-theme-default';
  var storageKey = 'vcc-site-theme';
  var eventName = 'vcc:site-theme-change';
  var toggleSelector = '[data-vcc-global-theme-toggle]';

  if (!root.hasAttribute(attributeName)) return;

  function normalizeTheme(value) {
    return value === 'dark' || value === 'light' ? value : null;
  }

  var defaultTheme = normalizeTheme(root.getAttribute(defaultAttributeName)) || 'light';
  var currentTheme = normalizeTheme(root.getAttribute(attributeName)) || defaultTheme;
  var requestedTheme = currentTheme;
  var requestVersion = 0;
  var darkSurfacesPromise = null;

  function darkSurfacesReady() {
    var link = document.querySelector('[data-vcc-dark-surfaces]');
    return root.getAttribute('data-vcc-dark-surfaces-ready') === 'true' ||
      Boolean(link && link.sheet) ||
      Boolean(!link && root.classList.contains('shopify-design-mode'));
  }

  function ensureDarkSurfaces() {
    if (darkSurfacesReady()) return Promise.resolve(true);
    if (darkSurfacesPromise) return darkSurfacesPromise;

    var link = document.querySelector('[data-vcc-dark-surfaces]');
    if (!link) return Promise.resolve(false);

    darkSurfacesPromise = new Promise(function (resolve, reject) {
      var settled = false;

      function cleanup() {
        link.removeEventListener('load', complete);
        link.removeEventListener('error', fail);
      }

      function complete() {
        if (settled) return;
        settled = true;
        cleanup();
        root.removeAttribute('data-vcc-dark-surfaces-error');
        root.setAttribute('data-vcc-dark-surfaces-ready', 'true');
        resolve(true);
      }

      function fail() {
        if (settled) return;
        settled = true;
        cleanup();
        darkSurfacesPromise = null;
        root.removeAttribute('data-vcc-dark-surfaces-ready');
        root.setAttribute('data-vcc-dark-surfaces-error', 'true');
        link.removeAttribute('href');
        reject(new Error('Dark surface stylesheet could not be loaded.'));
      }

      link.setAttribute('fetchpriority', 'high');
      link.addEventListener('load', complete, { once: true });
      link.addEventListener('error', fail, { once: true });
      if (!link.href && link.dataset.href) link.href = link.dataset.href;
      if (link.sheet) complete();
    });

    return darkSurfacesPromise;
  }

  function getTheme() {
    return normalizeTheme(root.getAttribute(attributeName)) || defaultTheme;
  }

  function findToggles(scope) {
    var toggles = [];
    var searchRoot = scope && typeof scope.querySelectorAll === 'function' ? scope : document;

    if (scope && typeof scope.matches === 'function' && scope.matches(toggleSelector)) toggles.push(scope);
    searchRoot.querySelectorAll(toggleSelector).forEach(function (toggle) {
      toggles.push(toggle);
    });

    return toggles;
  }

  function syncToggles(scope) {
    var isDark = getTheme() === 'dark';

    findToggles(scope).forEach(function (toggle) {
      toggle.checked = isDark;
      toggle.setAttribute('aria-checked', isDark ? 'true' : 'false');
      toggle.setAttribute('aria-label', isDark ? 'Switch to light mode' : 'Switch to dark mode');

      var control = toggle.closest('[data-vcc-global-theme-control]');
      if (!control) return;
      control.setAttribute('title', isDark ? 'Switch to light mode' : 'Switch to dark mode');

      var label = control.querySelector('[data-vcc-global-theme-label]');
      if (label) label.textContent = isDark ? 'Dark mode on' : 'Dark mode off';
    });
  }

  function saveTheme(theme) {
    try {
      window.localStorage.setItem(storageKey, theme);
    } catch (error) {}
  }

  function announceTheme(theme, previousTheme, source) {
    document.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        theme: theme,
        previousTheme: previousTheme,
        source: source
      }
    }));
  }

  function applyTheme(value, options) {
    var nextTheme = normalizeTheme(value) || defaultTheme;
    var previousTheme = currentTheme;
    var config = options || {};

    currentTheme = nextTheme;
    if (root.getAttribute(attributeName) !== nextTheme) root.setAttribute(attributeName, nextTheme);
    if (config.persist === true) saveTheme(nextTheme);
    syncToggles(document);

    if (nextTheme !== previousTheme || config.force === true) {
      announceTheme(nextTheme, previousTheme, config.source || 'api');
    }

    return nextTheme;
  }

  function requestTheme(value, options) {
    var nextTheme = normalizeTheme(value) || defaultTheme;
    var config = options || {};
    requestedTheme = nextTheme;
    requestVersion += 1;
    var version = requestVersion;

    if (nextTheme !== 'dark' || darkSurfacesReady()) {
      return applyTheme(nextTheme, config);
    }

    // Keep the currently complete light presentation in place until the full
    // dark owner is ready. Saved-dark startup is already dark under the inline
    // critical shell, so it is never forced back through a light frame here.
    if (currentTheme !== 'dark') syncToggles(document);
    ensureDarkSurfaces()
      .catch(function () { return false; })
      .then(function (ready) {
        if (ready !== true || version !== requestVersion || requestedTheme !== 'dark') {
          syncToggles(document);
          return;
        }
        applyTheme('dark', config);
      });
    return nextTheme;
  }

  function toggleTheme(options) {
    return requestTheme(getTheme() === 'dark' ? 'light' : 'dark', options || { persist: true, source: 'api' });
  }

  document.addEventListener('change', function (event) {
    var toggle = event.target && typeof event.target.matches === 'function' && event.target.matches(toggleSelector)
      ? event.target
      : null;
    if (!toggle) return;

    requestTheme(toggle.checked ? 'dark' : 'light', {
      persist: true,
      source: 'customer'
    });
  });

  window.addEventListener('storage', function (event) {
    if (event.key !== storageKey) return;
    requestTheme(normalizeTheme(event.newValue) || defaultTheme, {
      persist: false,
      source: 'storage'
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    syncToggles(event.target || document);
  });

  var attributeObserver = new MutationObserver(function () {
    var observedTheme = normalizeTheme(root.getAttribute(attributeName));
    if (!observedTheme) {
      applyTheme(defaultTheme, { persist: false, source: 'attribute' });
      return;
    }

    if (observedTheme === currentTheme) {
      return;
    }

    if (observedTheme === 'dark' && !darkSurfacesReady() && currentTheme !== 'dark') {
      root.setAttribute(attributeName, currentTheme);
      requestTheme('dark', { persist: false, source: 'attribute' });
      return;
    }

    var previousTheme = currentTheme;
    currentTheme = observedTheme;
    requestedTheme = observedTheme;
    syncToggles(document);
    announceTheme(observedTheme, previousTheme, 'attribute');
  });

  attributeObserver.observe(root, {
    attributes: true,
    attributeFilter: [attributeName]
  });

  window.VccGlobalTheme = {
    eventName: eventName,
    storageKey: storageKey,
    getTheme: getTheme,
    setTheme: function (theme, options) {
      var config = options || {};
      return requestTheme(theme, {
        persist: config.persist !== false,
        source: config.source || 'api',
        force: config.force === true
      });
    },
    toggleTheme: function (options) {
      var config = options || {};
      return toggleTheme({
        persist: config.persist !== false,
        source: config.source || 'api',
        force: config.force === true
      });
    },
    syncToggles: syncToggles
  };

  applyTheme(currentTheme, { persist: false, source: 'init' });
  if (currentTheme === 'dark') {
    ensureDarkSurfaces().catch(function () {
      if (currentTheme !== 'dark' || darkSurfacesReady()) return;
      requestedTheme = 'light';
      applyTheme('light', { persist: false, source: 'stylesheet-error' });
    });
  }
})();
